import {
  BoxProps,
  Button,
  Center,
  Checkbox,
  Divider,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  VStack,
} from '@elkaso-app/web-design';
import {
  useGetRelationBranchesApi,
  useGetRelationWarehousesApi,
  useSyncRelationBranchesApi,
  useSyncRelationWarehousesApi,
} from 'apis/restaurant-suppliers';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

type Entity = 'branches' | 'warehouses';

export interface IEntityMappingModalCell extends BoxProps {
  restaurantId: number;
  entity: Entity;
  trigger: JSX.Element;
}

const COPY: Record<Entity, { title: string; empty: string }> = {
  branches: { title: 'Map Branches', empty: 'No branches found' },
  warehouses: { title: 'Map Warehouses', empty: 'No warehouses found' },
};

export const EntityMappingModalCell = ({ restaurantId, entity, trigger }: IEntityMappingModalCell) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { supplierId } = useParams<{ supplierId: string }>();
  const supplierIdNum = parseInt(supplierId);

  // Both hooks are always called (rules of hooks); only the active entity's query is enabled.
  const branchesQuery = useGetRelationBranchesApi(restaurantId, supplierIdNum, {
    enabled: isOpen && entity === 'branches',
  });
  const warehousesQuery = useGetRelationWarehousesApi(restaurantId, supplierIdNum, {
    enabled: isOpen && entity === 'warehouses',
  });
  const activeQuery = entity === 'branches' ? branchesQuery : warehousesQuery;

  const syncBranches = useSyncRelationBranchesApi();
  const syncWarehouses = useSyncRelationWarehousesApi();
  const activeSync = entity === 'branches' ? syncBranches : syncWarehouses;

  const items = activeQuery.data?.data ?? [];
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Initialize checkboxes from isAssigned whenever fresh data arrives or the modal opens.
  useEffect(() => {
    if (isOpen && activeQuery.data?.data) {
      setSelectedIds(activeQuery.data.data.filter((item) => item.isAssigned).map((item) => item.id));
    }
  }, [isOpen, activeQuery.data]);

  const allChecked = items.length > 0 && selectedIds.length === items.length;
  const isIndeterminate = selectedIds.length > 0 && !allChecked;

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? items.map((item) => item.id) : []);
  };

  const toggleOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const handleSync = () => {
    activeSync.mutate(
      { restaurantId, supplierId: supplierIdNum, ids: selectedIds },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size='xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading as='h3' size='lg'>
              {COPY[entity].title}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            {activeQuery.isLoading && (
              <Center py='xl'>
                <Spinner />
              </Center>
            )}

            {!activeQuery.isLoading && items.length === 0 && (
              <Center py='xl'>
                <Text color='gray.500'>{COPY[entity].empty}</Text>
              </Center>
            )}

            {!activeQuery.isLoading && items.length > 0 && (
              <VStack align='stretch' spacing='sm'>
                <Checkbox
                  isChecked={allChecked}
                  isIndeterminate={isIndeterminate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleAll(e.target.checked)}
                  fontWeight='semibold'>
                  Select all
                </Checkbox>
                <Divider />
                <VStack align='stretch' spacing='sm' maxH='360px' overflowY='auto'>
                  {items.map((item) => (
                    <Checkbox
                      key={item.id}
                      isChecked={selectedIds.includes(item.id)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleOne(item.id, e.target.checked)}>
                      {item.name}
                    </Checkbox>
                  ))}
                </VStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='ghost' colorScheme='gray' onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                onClick={handleSync}
                isLoading={activeSync.isLoading}
                isDisabled={activeQuery.isLoading}>
                Sync
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
